import { z } from "zod";
import { jobBookingModeSchema, jobIdSchema, jobPricingUnitSchema } from "./job-schemas";

export const providerBookingIdSchema = jobIdSchema;
export const providerBookingStatusSchema = z.enum([
  "BOOKED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
]);
export const providerBookingAssignmentStatusSchema = z.enum([
  "ASSIGNED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
]);

const isoDateTimeSchema = z.string().datetime({ offset: true });
const versionNoSchema = z.number().int().min(1).max(2_147_483_647);
const currencySchema = z.literal("USD");

export const providerBookingServiceSchema = z.strictObject({
  serviceId: jobIdSchema,
  serviceCode: z.string().min(1).max(100),
  serviceName: z.string().min(1).max(200),
  pricingUnit: jobPricingUnitSchema,
  quantity: z.number().int().min(1).max(100),
});

export const providerBookingServiceAddressSchema = z.strictObject({
  line1: z.string().min(1).max(200),
  line2: z.string().min(1).max(200).nullable(),
  locality: z.string().min(1).max(100),
  regionCode: z.string().min(1).max(100),
  postalCode: z.string().min(1).max(30),
  countryCode: z.string().regex(/^[A-Z]{2}$/u),
});

const providerBookingCommonFields = {
  id: providerBookingIdSchema,
  jobNumber: z.string().regex(/^WBJ-[0-9A-F]{26}$/u),
  bookingMode: jobBookingModeSchema,
  jobStatus: providerBookingStatusSchema,
  jobVersionNo: versionNoSchema,
  assignmentStatus: providerBookingAssignmentStatusSchema,
  currency: currencySchema,
  acceptedAmountMinor: z.number().int().min(100).max(1_000_000),
  scheduledStartAt: isoDateTimeSchema,
  scheduledEndAt: isoDateTimeSchema,
  timezoneRegion: z.string().min(1).max(64),
  bookedAt: isoDateTimeSchema,
  startedAt: isoDateTimeSchema.nullable(),
  endedAt: isoDateTimeSchema.nullable(),
} as const;

function validateBookingWindow(
  booking: { scheduledStartAt: string; scheduledEndAt: string },
  context: z.RefinementCtx,
): void {
  if (Date.parse(booking.scheduledEndAt) <= Date.parse(booking.scheduledStartAt)) {
    context.addIssue({
      code: "custom",
      path: ["scheduledEndAt"],
      message: "Scheduled end must follow scheduled start",
    });
  }
}

export const providerBookingSummarySchema = z
  .strictObject(providerBookingCommonFields)
  .superRefine(validateBookingWindow);

export const providerBookingDetailSchema = z
  .strictObject({
    ...providerBookingCommonFields,
    serviceAddress: providerBookingServiceAddressSchema,
    specialInstructions: z.string().min(1).max(4_000).nullable(),
    needsLinen: z.boolean(),
    linenSetCount: z.number().int().min(0).max(9_999),
    needsTowels: z.boolean(),
    towelSetCount: z.number().int().min(0).max(9_999),
    services: z.array(providerBookingServiceSchema).min(1).max(100),
  })
  .superRefine((booking, context) => {
    validateBookingWindow(booking, context);
    if (!booking.needsLinen && booking.linenSetCount !== 0) {
      context.addIssue({
        code: "custom",
        path: ["linenSetCount"],
        message: "Linen count must be zero when linen is not needed",
      });
    }
    if (booking.needsLinen && booking.linenSetCount < 1) {
      context.addIssue({
        code: "custom",
        path: ["linenSetCount"],
        message: "Linen count is required when linen is needed",
      });
    }
    const serviceIds = new Set<string>();
    booking.services.forEach(({ serviceId }, index) => {
      if (serviceIds.has(serviceId)) {
        context.addIssue({
          code: "custom",
          path: ["services", index, "serviceId"],
          message: "Booked service IDs must be unique",
        });
      }
      serviceIds.add(serviceId);
    });
    if (!booking.needsTowels && booking.towelSetCount !== 0) {
      context.addIssue({
        code: "custom",
        path: ["towelSetCount"],
        message: "Towel count must be zero when towels are not needed",
      });
    }
    if (booking.needsTowels && booking.towelSetCount < 1) {
      context.addIssue({
        code: "custom",
        path: ["towelSetCount"],
        message: "Towel count is required when towels are needed",
      });
    }
  });

export const providerBookingActionInputSchema = z.strictObject({
  jobVersionNo: versionNoSchema,
});

export const providerBookingListResponseSchema = z.strictObject({
  status: z.literal("ok"),
  bookings: z.array(providerBookingSummarySchema).max(50),
});

export const providerBookingResponseSchema = z.strictObject({
  status: z.literal("ok"),
  booking: providerBookingDetailSchema,
});

export const providerBookingActionResponseSchema = z.strictObject({
  status: z.enum(["started", "completed", "cancelled", "existing"]),
  booking: providerBookingDetailSchema,
});

export type ProviderBookingStatus = z.infer<typeof providerBookingStatusSchema>;
export type ProviderBookingAssignmentStatus = z.infer<typeof providerBookingAssignmentStatusSchema>;
export type ProviderBookingService = z.infer<typeof providerBookingServiceSchema>;
export type ProviderBookingServiceAddress = z.infer<typeof providerBookingServiceAddressSchema>;
export type ProviderBookingSummary = z.infer<typeof providerBookingSummarySchema>;
export type ProviderBookingDetail = z.infer<typeof providerBookingDetailSchema>;
export type ProviderBookingActionInput = z.infer<typeof providerBookingActionInputSchema>;
export type ProviderBookingActionResponse = z.infer<typeof providerBookingActionResponseSchema>;
